// IMA-ADPCM wie im Studio (studio-rs/crates/wrstudio-link/src/codec.rs), Byte für Byte gleich.
// Die Fabrik ist bewusst in sich geschlossen: Der Audio-Worklet bekommt sie als Quelltext.

export const LINK_RATE = 32000;
export const FRAME_SAMPLES = 640;
export const PAYLOAD_BYTES = 4 + FRAME_SAMPLES / 2;

export interface Codec {
  /** Kodiert genau 640 Samples (−1..1); der Schrittweiten-Index läuft über Rahmen weiter. */
  encode(samples: Float32Array): Uint8Array;
  /** Dekodiert einen Rahmen oder liefert null bei falscher Länge. */
  decode(payload: Uint8Array): Float32Array | null;
}

export function makeCodec(): Codec {
  const FRAME = 640;
  const INDEX = [-1, -1, -1, -1, 2, 4, 6, 8, -1, -1, -1, -1, 2, 4, 6, 8];
  const STEP = [
    7, 8, 9, 10, 11, 12, 13, 14, 16, 17, 19, 21, 23, 25, 28, 31, 34, 37, 41, 45, 50, 55, 60, 66, 73, 80, 88, 97, 107, 118, 130, 143,
    157, 173, 190, 209, 230, 253, 279, 307, 337, 371, 408, 449, 494, 544, 598, 658, 724, 796, 876, 963, 1060, 1166, 1282, 1411,
    1552, 1707, 1878, 2066, 2272, 2499, 2749, 3024, 3327, 3660, 4026, 4428, 4871, 5358, 5894, 6484, 7132, 7845, 8630, 9493, 10442,
    11487, 12635, 13899, 15289, 16818, 18500, 20350, 22385, 24623, 27086, 29794, 32767,
  ];
  let encIndex = 0;
  const toI16 = (v: number): number => Math.round(Math.max(-1, Math.min(1, v)) * 32767);
  const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

  return {
    encode(samples: Float32Array): Uint8Array {
      const out = new Uint8Array(4 + FRAME / 2);
      let predictor = toI16(samples[0]!);
      let index = clamp(encIndex, 0, 88);
      out[0] = predictor & 0xff;
      out[1] = (predictor >> 8) & 0xff;
      out[2] = index;
      out[3] = 0;
      let byte = 0;
      for (let i = 0; i < FRAME; i++) {
        const step = STEP[index]!;
        let diff = toI16(samples[i]!) - predictor;
        let code = 0;
        if (diff < 0) {
          code = 8;
          diff = -diff;
        }
        let delta = step >> 3;
        if (diff >= step) {
          code |= 4;
          diff -= step;
          delta += step;
        }
        if (diff >= step >> 1) {
          code |= 2;
          diff -= step >> 1;
          delta += step >> 1;
        }
        if (diff >= step >> 2) {
          code |= 1;
          delta += step >> 2;
        }
        predictor = clamp(code & 8 ? predictor - delta : predictor + delta, -32768, 32767);
        index = clamp(index + INDEX[code]!, 0, 88);
        if (i % 2 === 0) byte = code;
        else out[4 + (i >> 1)] = byte | (code << 4);
      }
      encIndex = index;
      return out;
    },
    decode(payload: Uint8Array): Float32Array | null {
      if (payload.length !== 4 + FRAME / 2 || payload[2]! > 88) return null;
      const out = new Float32Array(FRAME);
      let predictor = (payload[0]! | (payload[1]! << 8)) << 16 >> 16;
      let index = payload[2]!;
      for (let i = 0; i < FRAME; i++) {
        const b = payload[4 + (i >> 1)]!;
        const code = i % 2 === 0 ? b & 0x0f : b >> 4;
        const step = STEP[index]!;
        let delta = step >> 3;
        if (code & 4) delta += step;
        if (code & 2) delta += step >> 1;
        if (code & 1) delta += step >> 2;
        predictor = clamp(code & 8 ? predictor - delta : predictor + delta, -32768, 32767);
        index = clamp(index + INDEX[code]!, 0, 88);
        out[i] = predictor / 32768;
      }
      return out;
    },
  };
}
